import React from 'react';
import { useHistory } from 'react-router-dom';
import { getFrameOrigin, withFrameOrigin } from './frame-origin';
import { withSessionlessHint } from './sessionless-hint';

// Leave the URL alone while auth-js reads the code/state from it.
const AUTH_CALLBACK_PATH = '/auth/callback';

/**
 * Keeps `frameOrigin` in the address bar across in-app navigation while we are framed.
 *
 * Router navigation does not refetch the document, but a later reload of the frame does, and it
 * requests whatever URL the address bar holds by then. Without the param that request presents no
 * partner, the gateway leaves it out of frame-ancestors, and a cookie-blocked frame is refused
 * before any of our code can run (AP-3437). Replacing, not pushing, keeps history unchanged.
 *
 * The `sessionless` hint rides along for the same reason: a reload must still know that this frame
 * signs in with a token from the widget (AP-3360).
 */
export function RetainFrameOrigin(): null {
  const history = useHistory();
  type RouterLocation = typeof history.location;

  React.useEffect(() => {
    function retain(location: RouterLocation) {
      if (!getFrameOrigin()) return;
      if (location.pathname.includes(AUTH_CALLBACK_PATH)) return;

      const current = `${location.pathname}${location.search}${location.hash}`;
      const next = withSessionlessHint(withFrameOrigin(current));
      // The replace fires this listener again; by then the param is present and next === current.
      if (next !== current) history.replace(next, location.state);
    }

    retain(history.location);
    return history.listen(retain);
  }, [history]);

  return null;
}
